import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Bot, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const CUISINES = [
  "Mediterranean", "Middle Eastern", "Asian", "Indian",
  "Italian", "Mexican", "American", "Vegetarian", "Vegan", "Keto",
];
const MEAL_TYPES = [
  { key: "breakfast", label: "Breakfast" },
  { key: "lunch", label: "Lunch" },
  { key: "dinner", label: "Dinner" },
  { key: "snacks", label: "Snacks" },
];

export default function AiGenerateDialog({ patientId, planType, onGenerated, disabled }) {
  const [open, setOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({
    target_calories: 1800,
    protein_pct: 30,
    carbs_pct: 40,
    fat_pct: 30,
    days: 7,
    cuisines: [],
    meal_types: ["breakfast", "lunch", "dinner", "snacks"],
    extra_notes: "",
  });

  const setF = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const toggleCuisine = (c) => {
    setForm((p) => ({
      ...p,
      cuisines: p.cuisines.includes(c) ? p.cuisines.filter((x) => x !== c) : [...p.cuisines, c],
    }));
  };

  const toggleMeal = (m) => {
    setForm((p) => ({
      ...p,
      meal_types: p.meal_types.includes(m) ? p.meal_types.filter((x) => x !== m) : [...p.meal_types, m],
    }));
  };

  const macroSum = Number(form.protein_pct) + Number(form.carbs_pct) + Number(form.fat_pct);
  const macroOk = macroSum === 100;
  const canSubmit = patientId && form.target_calories >= 800 && form.meal_types.length > 0 && macroOk && !generating;

  const submit = async () => {
    if (!patientId) { toast.error("Select a patient first"); return; }
    if (!macroOk) { toast.error("Macro percentages must sum to 100"); return; }
    if (form.meal_types.length === 0) { toast.error("Select at least one meal type"); return; }
    setGenerating(true);
    try {
      const { data } = await api.post("/diet-plans/ai-generate", {
        patient_id: patientId,
        plan_type: planType,
        target_calories: Number(form.target_calories),
        protein_pct: Number(form.protein_pct),
        carbs_pct: Number(form.carbs_pct),
        fat_pct: Number(form.fat_pct),
        days: Number(form.days),
        cuisines: form.cuisines,
        meal_types: form.meal_types,
        extra_notes: form.extra_notes,
      }, { timeout: 120000 });
      onGenerated?.(data);
      setOpen(false);
      toast.success("AI plan ready — review and customize before saving");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" disabled={disabled} className="gap-2 rounded-full" data-testid="open-ai-generate">
          <Bot size={14} /> Generate with AI
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="ai-generate-dialog">
        <DialogHeader>
          <DialogTitle>Generate plan with AI</DialogTitle>
          <DialogDescription>
            Claude Sonnet 4.5 drafts a {planType === "general" ? "general structure" : "detailed daily plan"} using
            only foods from your database and the patient's profile. You can edit anything before saving.
          </DialogDescription>
        </DialogHeader>

        {!patientId && (
          <div className="rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 text-sm px-3 py-2" data-testid="ai-needs-patient">
            ⚠️ Select a patient first — the AI uses their profile (allergies, conditions, preferences) to tailor the plan.
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="space-y-1 col-span-2 sm:col-span-2">
            <Label className="text-xs">Daily calories</Label>
            <Input type="number" min={800} max={5000} value={form.target_calories} onChange={(e) => setF("target_calories", e.target.value)} data-testid="ai-calories" />
          </div>
          {planType === "detailed" && (
            <div className="space-y-1 col-span-2 sm:col-span-2">
              <Label className="text-xs">Number of days (1-14)</Label>
              <Input type="number" min={1} max={14} value={form.days} onChange={(e) => setF("days", e.target.value)} data-testid="ai-days" />
            </div>
          )}
        </div>

        <div>
          <Label className="text-xs">Macro split (must total 100%)</Label>
          <div className="grid grid-cols-3 gap-2 mt-1">
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Protein</Label>
              <Input type="number" min={0} max={100} value={form.protein_pct} onChange={(e) => setF("protein_pct", e.target.value)} data-testid="ai-protein-pct" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Carbs</Label>
              <Input type="number" min={0} max={100} value={form.carbs_pct} onChange={(e) => setF("carbs_pct", e.target.value)} data-testid="ai-carbs-pct" />
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-muted-foreground">Fat</Label>
              <Input type="number" min={0} max={100} value={form.fat_pct} onChange={(e) => setF("fat_pct", e.target.value)} data-testid="ai-fat-pct" />
            </div>
          </div>
          <p className={`text-[11px] mt-1 ${macroOk ? "text-muted-foreground" : "text-destructive"}`} data-testid="ai-macro-sum">
            Total: {macroSum}% {macroOk ? "✓" : "(must equal 100%)"}
          </p>
        </div>

        <div>
          <Label className="text-xs">Meal types to include</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {MEAL_TYPES.map((m) => {
              const active = form.meal_types.includes(m.key);
              return (
                <button
                  key={m.key}
                  type="button"
                  onClick={() => toggleMeal(m.key)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input hover:border-primary/50"}`}
                  data-testid={`ai-meal-${m.key}`}
                >
                  {active && <Check size={12} />}
                  {m.label}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <Label className="text-xs">Preferred cuisines <span className="text-muted-foreground">(optional, select multiple)</span></Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {CUISINES.map((c) => {
              const active = form.cuisines.includes(c);
              return (
                <button
                  key={c}
                  type="button"
                  onClick={() => toggleCuisine(c)}
                  className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs border transition-colors ${active ? "bg-primary text-primary-foreground border-primary" : "bg-background border-input hover:border-primary/50"}`}
                  data-testid={`ai-cuisine-${c.replace(/\s+/g, "_").toLowerCase()}`}
                >
                  {active && <Check size={12} />}
                  {c}
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-1">
          <Label className="text-xs">Extra notes for the AI <span className="text-muted-foreground">(optional)</span></Label>
          <Textarea rows={3} placeholder="e.g. keep dinner light, no breakfast smoothies, prefer fish over red meat" value={form.extra_notes} onChange={(e) => setF("extra_notes", e.target.value)} data-testid="ai-extra-notes" />
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-border">
          <Button type="button" variant="outline" onClick={() => setOpen(false)} disabled={generating} data-testid="ai-cancel">
            Cancel
          </Button>
          <Button type="button" onClick={submit} disabled={!canSubmit} className="gap-2 rounded-full" data-testid="ai-submit">
            {generating ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
            {generating ? "Generating…" : "Generate plan"}
          </Button>
        </div>
        <p className="text-[11px] text-muted-foreground">
          The AI is restricted to your food database. Always review the draft before saving.
        </p>
      </DialogContent>
    </Dialog>
  );
}
